// RichText sanitization — DOMPurify wrapped to a strict allowlist, returning
// an HtmlString suitable for direct interpolation into email output.
// Per spec/tech-arch.md §12.1 + spec/operating-contract.md §7.1.

import DOMPurifyDefault, { type WindowLike } from 'dompurify'
import { HtmlString } from '../types'

// DOMPurify v3's default export is dual-shape. When a `window` is global at
// module load — browser bundles via Vite, Vitest with the happy-dom env per
// D-007 — it is pre-bound and exposes `.sanitize` directly. In pure-Node
// contexts (the Playwright runner calls `block.render()` from Node to
// capture HTML for snapshotting, which triggers `sanitizeRichText`) no
// `window` is global, so the default export is the unbound factory
// `(window) => DOMPurify`. Detect the unbound shape and bind via happy-dom's
// `Window` (already a devDep for Vitest; no new dependency). Browser bundles
// take the first arm; the second arm runs under the Playwright runner, which
// is exercised by the visual-regression suite (not Vitest), so it doesn't
// register in v8 coverage. See D-056.
//
// **The `/* @vite-ignore */` magic comment** on the dynamic `import('happy-dom')`
// stops Rollup from attempting to bundle happy-dom into the browser build.
// The runtime guard ensures the import never fires in the browser (DOMPurify
// is pre-bound there), so the bundle only references the module as an
// external dynamic specifier. Without the comment, Rollup walks happy-dom's
// dependency graph at build time — which transitively pulls in Node built-ins
// (`buffer`, `stream/web`, etc.) and breaks the build. Surfaced at P-024 when
// the Library screen pulled the full Block registry into the import graph,
// which transitively imports this module.
let DOMPurify = DOMPurifyDefault
/* v8 ignore next 4 -- Node-only rebind, exercised by Playwright runner */
if (typeof DOMPurifyDefault.sanitize !== 'function') {
  const { Window } = await import(/* @vite-ignore */ 'happy-dom')
  DOMPurify = DOMPurifyDefault(new Window() as unknown as WindowLike)
}

// Strict allowlist matches Tiptap's configured marks (P-028 + P-037.28):
// paragraph, bold, italics, link, line break, plus bulleted + ordered
// lists (D-136). Anything else — headings, color, in-line style attrs,
// scripts, iframes — is stripped. Loosening requires a Decision Log entry
// per build-plan P-004 row.
//
// **P-037.28 D-136 — `ul` + `ol` + `li` added.** Tiptap's StarterKit
// BulletList + OrderedList + ListItem nodes were flipped ON for the
// new atomic `text` Block (item 13 revised) and inherited by the
// `time-location-cost` Block's 3 column bodies (item 18). The list
// tags carry no XSS-relevant attribute surface: DOMPurify still
// rejects all attributes outside ALLOWED_ATTR (`href`, `target`,
// `rel`), so a malicious `<li onclick="…">` becomes `<li>`. The
// existing fixed-point-loop discipline (sanitizeUntilStable) catches
// nested-wrapper attacks. No URL-attr surface added — list tags
// don't carry hrefs.
//
// On `ADD_URI_SAFE_ATTR`: DOMPurify's default URI_SAFE_ATTRIBUTES list does
// NOT include `target` or `rel`, so without this they'd be URL-validated
// against ALLOWED_URI_REGEXP (which only matches `https?:|mailto:`) and
// stripped — `_blank` and `noopener` aren't URLs. Marking them URI-safe
// bypasses URL validation while keeping them allowlisted.
//
// `ALLOW_DATA_ATTR` and `ALLOW_ARIA_ATTR` default true; we explicitly
// disable both — richText input shouldn't carry data-* / aria-* either,
// and disabling closes a class of injection vectors.
const DOMPURIFY_CONFIG = {
  ALLOWED_TAGS: ['p', 'strong', 'em', 'a', 'br', 'ul', 'ol', 'li'],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
  ALLOWED_URI_REGEXP: /^(?:https?:|mailto:)/i,
  ADD_URI_SAFE_ATTR: ['target', 'rel'],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
}

// DOMPurify's strict allowlist mode has a known quirk: when a parent element
// is stripped (because it's not in ALLOWED_TAGS), its kept-content children
// are NOT re-evaluated against the allowlist on the same pass. E.g.,
// `<ul><li>x</li></ul>` → `<li>x</li>` after one pass; even worse,
// `<div><script>alert(1)</script></div>` → `<script>alert(1)</script>` —
// the script is lifted intact and only stripped on the next pass. Each
// nesting level burns one pass, so deeply nested wrappers around a
// dangerous tag can exceed any iteration cap. We loop until output stops
// changing and **fail closed** (return empty) on non-convergence, never
// returning a partially-sanitized string as trusted output. See D-022.
const MAX_SANITIZE_PASSES = 20

function sanitizeUntilStable(input: string): string {
  let prev: string
  let curr = input
  let passes = 0
  do {
    prev = curr
    curr = DOMPurify.sanitize(prev, DOMPURIFY_CONFIG)
    passes++
  } while (curr !== prev && passes < MAX_SANITIZE_PASSES)
  // Non-convergence under the cap → fail closed. Surfacing the input as
  // empty is safer than returning still-dangerous markup.
  if (curr !== prev) return ''
  return curr
}

export function sanitizeRichText(input: string): HtmlString {
  // Empty input is common (optional richText fields) — skip DOMPurify entirely.
  if (input === '') return new HtmlString('')
  return new HtmlString(sanitizeUntilStable(input))
}
